import {
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  ExternalLink,
  CornerUpLeft,
  Phone,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  BodySegment,
  PreviewRecipient,
  PreviewTemplate,
  SendRequirements,
  fillTemplateValue,
  resolveBodySegments,
  resolvePreviewButtons,
} from '@/lib/whatsappPreview';

// The message as WhatsApp will render it.
//
// WHY IT EXISTS. Everything that decides what actually goes out — which template, which of the
// campaign's images fills its header, what a body variable resolves to for a given person — was
// previously spread across a template dropdown, a media picker and a box of raw body text with
// `{{1}}` still in it. Nobody could see the message until it had been sent to somebody.
//
// This component is PURE: every input is a prop and it fetches nothing. That is deliberate — it
// renders in the draft editor against live form state and on a launched campaign against what was
// actually recorded, and those two callers have completely different ideas of where data comes
// from.
//
// It renders in `buildTemplateComponents` order (header, body, footer, buttons) and follows the
// same rules, because a preview that disagrees with the sender is worse than none.

interface HeaderMediaPreview {
  /** Defaults to the kind the template's header format implies. */
  kind?: 'photo' | 'video' | 'document';
  /** A signed thumbnail or a local object URL, when one exists. Absent for a file WhatsApp holds only by id. */
  previewUrl?: string | null;
  label?: string | null;
  note?: string | null;
}

interface WhatsAppMessagePreviewProps {
  template?: PreviewTemplate | null;
  /** From GET /whatsapp-templates/:id — the only source that knows a header or button holds {{n}}. */
  requirements?: SendRequirements | null;
  /** Whose values to resolve body variables against. Null renders annotated placeholders. */
  recipient?: PreviewRecipient | null;
  headerMedia?: HeaderMediaPreview | null;
  /** The header image or video failed to load — a signed URL that has expired, usually. */
  onMediaError?: () => void;
  /** Fills a {{n}} in a TEXT header. One value for the whole campaign, not per recipient. */
  headerText?: string;
  buttonValues?: Record<string, string> | null;
  /** SMS mode: the same bubble with no header, footer or buttons. */
  smsBody?: string | null;
  className?: string;
}

const MEDIA_ICON = {
  photo: ImageIcon,
  video: VideoIcon,
  document: FileText,
} as const;

const MEDIA_LABEL = {
  photo: 'IMAGE',
  video: 'VIDEO',
  document: 'DOCUMENT',
} as const;

/** Which generated asset kind backs each header format. Mirrors ASSET_KIND_FOR_FORMAT. */
const KIND_FOR_FORMAT: Record<string, NonNullable<HeaderMediaPreview['kind']>> = {
  IMAGE: 'photo',
  VIDEO: 'video',
  DOCUMENT: 'document',
};

/** A placeholder chip for a variable that has no value to show. */
const VariableChip = ({ index, label, tone }: { index: string; label?: string; tone: 'neutral' | 'warn' | 'error' }) => (
  <span
    className={cn(
      'inline-flex items-center rounded px-1 py-0.5 text-[11px] font-medium align-baseline',
      tone === 'neutral' && 'bg-muted text-muted-foreground',
      tone === 'warn' && 'bg-amber-100 text-amber-900',
      tone === 'error' && 'bg-destructive/15 text-destructive',
    )}
    title={label ? `{{${index}}} — ${label}` : `{{${index}}}`}
  >
    {label || `{{${index}}}`}
  </span>
);

const renderSegment = (segment: BodySegment, key: number) => {
  switch (segment.kind) {
    case 'text':
      return <span key={key}>{segment.text}</span>;
    case 'value':
      return <span key={key}>{segment.text}</span>;
    case 'empty':
      // Mapped, but this contact has nothing in that field. The send supplies an empty parameter
      // and WhatsApp rejects this recipient alone — a real outcome worth showing, not hiding.
      return <VariableChip key={key} index={segment.index} label={segment.label || segment.field} tone="warn" />;
    case 'missing':
      return <VariableChip key={key} index={segment.index} label={segment.label} tone="neutral" />;
    case 'unmapped':
      // No contact field behind this index: every message would be rejected for a parameter
      // mismatch, which is why the server and the Launch button both refuse it.
      return <VariableChip key={key} index={segment.index} label={segment.label} tone="error" />;
    default:
      return null;
  }
};

export const WhatsAppMessagePreview = ({
  template,
  requirements,
  recipient,
  headerMedia,
  onMediaError,
  headerText,
  buttonValues,
  smsBody,
  className,
}: WhatsAppMessagePreviewProps) => {
  // A carousel cannot be rendered as one bubble, and pretending otherwise would promise a send that
  // both the server and the Launch button refuse.
  if (requirements?.hasCarousel) {
    return (
      <div className={cn('rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900', className)}>
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4" />
          Carousel templates cannot be sent
        </div>
        <p className="mt-1 text-xs">
          This template uses a carousel, which this product does not support yet. Choose a template
          without one.
        </p>
      </div>
    );
  }

  const isSms = Boolean(smsBody != null);
  const segments = isSms ? [] : resolveBodySegments(template, recipient);
  const buttons = isSms ? [] : resolvePreviewButtons(template, requirements, buttonValues);

  // `requirements` is authoritative about the header; the flat `headerType` column is the fallback
  // for a template synced before the provider's raw component list was kept, where whether the
  // header text holds a {{n}} simply is not recoverable.
  const headerFormat = requirements?.header?.format || template?.headerType || 'NONE';
  const headerHasVariable = requirements?.header?.hasVariable ?? false;
  const headerBody = requirements?.header?.text ?? template?.headerContent ?? null;
  const mediaKind = headerMedia?.kind || KIND_FOR_FORMAT[headerFormat];
  const showMediaHeader = !isSms && Boolean(mediaKind) && headerFormat !== 'TEXT' && headerFormat !== 'NONE';
  const showTextHeader = !isSms && headerFormat === 'TEXT' && Boolean(headerBody);

  const headerFilled = showTextHeader
    ? fillTemplateValue(headerBody, headerHasVariable ? headerText : undefined)
    : null;

  const MediaIcon = mediaKind ? MEDIA_ICON[mediaKind] : ImageIcon;

  const isEmpty = !isSms && !template;

  return (
    // The FRAME is forced LTR while the content inside is dir="auto". A preview that mirrors its own
    // layout when the UI switches to Arabic reads as a rendering bug; the message text still lays
    // itself out correctly per node, which is what actually matters here.
    <div dir="ltr" className={cn('rounded-xl bg-[#0b141a] p-3', className)}>
      <div className="mx-auto max-w-sm">
        <div className="rounded-lg bg-[#1f2c34] p-2 shadow-sm">
          {isEmpty ? (
            <p className="p-6 text-center text-sm text-slate-400">
              Choose a template to see the message.
            </p>
          ) : (
            <>
              {showMediaHeader && (
                <div className="mb-2 overflow-hidden rounded-md bg-[#111b21]">
                  {headerMedia?.previewUrl && mediaKind === 'photo' && (
                    <img
                      src={headerMedia.previewUrl}
                      alt={headerMedia.label || 'Header image'}
                      className="aspect-[4/3] w-full object-cover"
                      onError={onMediaError}
                    />
                  )}
                  {headerMedia?.previewUrl && mediaKind === 'video' && (
                    <video
                      src={headerMedia.previewUrl}
                      className="aspect-[4/3] w-full object-cover"
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      onError={onMediaError}
                    />
                  )}
                  {(!headerMedia?.previewUrl || mediaKind === 'document') && (
                    // Correct, not a fallback: a file uploaded straight to WhatsApp is held by id
                    // with no URL we can render, and we generate no documents at all.
                    <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 text-slate-400">
                      <MediaIcon className="h-8 w-8" />
                      <span className="text-xs font-medium tracking-wide">
                        {MEDIA_LABEL[mediaKind || 'photo']}
                      </span>
                      {(headerMedia?.label || headerMedia?.note) && (
                        <span className="px-4 text-center text-[11px] text-slate-500" dir="auto">
                          {headerMedia.note || headerMedia.label}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showTextHeader && (
                <p className="px-2 pt-1 text-sm font-semibold text-slate-100" dir="auto">
                  {headerFilled?.filled}
                  {headerFilled?.hasBlank && (
                    <VariableChip index="1" label="header text" tone="neutral" />
                  )}
                </p>
              )}

              <p className="whitespace-pre-wrap px-2 py-1 text-sm text-slate-100" dir="auto">
                {isSms ? smsBody : segments.map(renderSegment)}
              </p>

              {!isSms && template?.footer && (
                <p className="px-2 pb-1 text-xs text-slate-400" dir="auto">
                  {template.footer}
                </p>
              )}

              {buttons.length > 0 && (
                <div className="mt-1 border-t border-white/10">
                  {buttons.map((button, index) => {
                    const Icon = button.type === 'URL'
                      ? ExternalLink
                      : button.type === 'PHONE_NUMBER'
                        ? Phone
                        : CornerUpLeft;

                    return (
                      <div key={`${button.text}-${index}`} className="border-b border-white/10 last:border-b-0">
                        <div className="flex items-center justify-center gap-1.5 py-2 text-sm text-[#53bdeb]">
                          <Icon className="h-3.5 w-3.5" />
                          <span dir="auto">{button.text}</span>
                        </div>
                        {button.type === 'URL' && (button.url || button.needsValue) && (
                          <p className="pb-1.5 text-center text-[10px] text-slate-500" dir="auto">
                            {button.needsValue
                              ? 'Link value not set'
                              : button.url}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppMessagePreview;
